// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { getPharmacy } from '../api/client'
import { useAuth } from './AuthContext'

/**
 * Active-pharmacy context. WI-893 SaaS-vs-pharmacy UI mode split.
 *
 * The UI runs in one of two modes:
 *
 *   'saas'     — The user is acting as a SaaS-level operator. Sees only
 *                cross-tenant tools (Pharmacies list, system audit, API keys,
 *                roadmap). NO pharmacy-scoped data (Rx, patients, etc).
 *
 *   'pharmacy' — The user is acting on behalf of one specific pharmacy.
 *                Sees Rx, patients, prescribers, reports, pharmacy settings
 *                for that pharmacy only. The pharmacy banner is visible in
 *                the topbar.
 *
 * Mode is determined by:
 *   1. If the logged-in account has a home pharmacy (me.pharmacy_id is set):
 *      always 'pharmacy', and the active pharmacy IS the home pharmacy.
 *      (Pharmacy users can't enter SaaS mode.)
 *
 *   2. If the logged-in account is unbound (me.pharmacy_id is null) AND
 *      the user has selected an active pharmacy via enterPharmacy():
 *      mode is 'pharmacy', active pharmacy is the selected one.
 *
 *   3. Otherwise (unbound account, no selection):
 *      mode is 'saas'.
 *
 * Persistence: the SaaS-admin's active pharmacy choice is kept in
 * localStorage so a refresh keeps them in the pharmacy view they were
 * looking at. The home-pharmacy case doesn't use localStorage (it's
 * derived from the account itself).
 */
export type UIMode = 'saas' | 'pharmacy'

export interface ActivePharmacy {
  id: string
  name: string
  slug: string
  logo_url?: string | null
  primary_color_hex?: string | null
  // Letterhead identity (D-CRX-67): the enrollment PDF restamps the tenant's own header, and a
  // header with a name but no address or phone is not a usable clinical document. Already present
  // on the pharmacy payload — it was simply being dropped here.
  address_line?: string | null
  city?: string | null
  state_code?: string | null
  zip_code?: string | null
  primary_phone?: string | null
  // D-CRX-76. Two letterhead defects, both invisible until a tenant fills the fields in:
  //   display_name - every SERVER-generated document reads COALESCE(display_name_override, name).
  //     The client-generated enrollment form read `name` alone, so a tenant with an override got
  //     one business name on its consent and a different one on its enrollment form.
  //   primary_fax  - the form has a Fax line that nothing could ever fill.
  display_name?: string | null
  primary_fax?: string | null
}

interface PharmacyState {
  mode: UIMode
  activePharmacy: ActivePharmacy | null
  /** True when the active pharmacy is a SaaS-admin override (vs the user's home). */
  isOverride: boolean
  enterPharmacy: (p: ActivePharmacy) => void
  exitPharmacy: () => void
}

const PharmacyContext = createContext<PharmacyState | null>(null)

const LS_KEY = 'owlrx.activePharmacy.v1'

// Dial-in override is per-tab/session (sessionStorage): it survives a refresh but is NOT carried
// across logins or new tabs. This prevents a stale "I dialed into pharmacy X" state from dropping
// a platform admin into a pharmacy on their next login. (Previously localStorage, which persisted
// across sessions and caused exactly that cross-login bleed.)
function readLS(): ActivePharmacy | null {
  try {
    const raw = sessionStorage.getItem(LS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed.id === 'string' && typeof parsed.name === 'string' && typeof parsed.slug === 'string') {
      return parsed as ActivePharmacy
    }
  } catch { /* swallow */ }
  return null
}

function writeLS(v: ActivePharmacy | null) {
  try {
    if (v) sessionStorage.setItem(LS_KEY, JSON.stringify(v))
    else sessionStorage.removeItem(LS_KEY)
  } catch { /* swallow */ }
}

// One-time purge of the old persisted override so existing browsers don't keep dropping the user
// into a stale dialed-in pharmacy after the switch to session-scoped overrides.
try { localStorage.removeItem(LS_KEY) } catch { /* swallow */ }

export function PharmacyProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth()
  const [override, setOverride] = useState<ActivePharmacy | null>(null)
  // WI-899: cache the home-pharmacy's name/slug so the topbar can display
  // it. /auth/me only returns pharmacy_id; we resolve to name via
  // /api/pharmacies/{id} once per session and store here.
  const [homePharmacy, setHomePharmacy] = useState<ActivePharmacy | null>(null)

  // Hydrate from localStorage on mount, but only if the user is unbound.
  // (A pharmacy user's home pharmacy supersedes any LS leftover.)
  // Stable primitive: the only thing this effect cares about from `permissions`.
  const hasPlatformAccess = !!me?.permissions?.includes('platform.access')

  useEffect(() => {
    if (!me?.authenticated) {
      setOverride(null)
      setHomePharmacy(null)
      return
    }
    if (me.pharmacy_id && !hasPlatformAccess) {
      // Home pharmacy user (not a platform admin); clear any SaaS-mode override
      setOverride(null)
      writeLS(null)
      // Resolve name from /api/pharmacies/{id}. This endpoint is now
      // tenant-scoped (WI-899) so it returns the user's own pharmacy
      // when they call it for their own pharmacy_id.
      void (async () => {
        // Retry with backoff: a single failed fetch (e.g. the portal loading during a
        // Function App deploy swap) previously left the topbar stuck on "Loading…" and
        // the logo missing for the whole session.
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            // RECURRING "Loading…" RCA (Patrick 2026-07-30, prior BioAccess 07-15):
            // this was a RAW fetch, which BYPASSES the api client's dead-token
            // self-heal (RCA-AUTH-1/T2). A stale per-tab Bearer 401'd all three
            // attempts identically while every api.request call on the page healed
            // and succeeded — banner stuck for the whole session. Route through the
            // client so a 401 heals the session and the retry actually differs.
            const data: any = await getPharmacy(me.pharmacy_id!)
            {
              const p = data?.pharmacy
              if (p?.id && p?.name) {
                setHomePharmacy({
                  id: p.id,
                  name: p.name,
                  slug: p.slug || '',
                  logo_url: p.logo_url || null,
                  primary_color_hex: p.primary_color_hex || null,
                  address_line: p.address_line || null,
                  city: p.city || null,
                  state_code: p.state_code || null,
                  zip_code: p.zip_code || null,
                  primary_phone: p.primary_phone || null,
                  display_name: p.display_name_override || p.name || null,
                  primary_fax: p.primary_fax || null,
                })
              }
              return
            }
          } catch { /* retry below; topbar falls back to id substring after final attempt */ }
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)))
        }
      })()
      return
    }
    // Unbound (SaaS) account OR platform admin; pick up override if any
    setHomePharmacy(null)
    setOverride(readLS())
    // PERF (RCA-003): me.permissions is an ARRAY, and React compares deps with
    // Object.is — a fresh array reference on every /api/auth/me refresh made this
    // effect re-run even when the contents were identical, re-fetching
    // /api/pharmacies/{id} each time. Patrick's HAR showed NINE calls in a single
    // page load. Depend on a stable primitive derived from the array instead, so
    // the effect fires only when the value that matters actually changes.
  }, [me?.authenticated, me?.pharmacy_id, hasPlatformAccess])

  const enterPharmacy = useCallback((p: ActivePharmacy) => {
    setOverride(p)
    writeLS(p)
  }, [])

  const exitPharmacy = useCallback(() => {
    setOverride(null)
    writeLS(null)
  }, [])

  // Compute mode + activePharmacy based on rules above
  let mode: UIMode = 'saas'
  let activePharmacy: ActivePharmacy | null = null
  let isOverride = false
  // Platform admins (the 'platform.access' permission) administer the Owl platform regardless
  // of any pharmacy binding: they default to SaaS mode and can dial into pharmacies — even if
  // their account has a home pharmacy_id. Pharmacy-only accounts stay locked to their pharmacy.
  const isPlatformAdmin = !!me?.permissions?.includes('platform.access')
  if (me?.authenticated) {
    if (me.pharmacy_id && !isPlatformAdmin) {
      // Home pharmacy user — always pharmacy mode.
      mode = 'pharmacy'
      activePharmacy = homePharmacy
        ?? { id: me.pharmacy_id, name: '', slug: '' }
      isOverride = false
    } else if (override) {
      // SaaS admin / platform admin who has dialed into a pharmacy.
      mode = 'pharmacy'
      activePharmacy = override
      isOverride = true
    } else {
      mode = 'saas'
      activePharmacy = null
      isOverride = false
    }
  }

  // WI-902: sync the browser tab title + favicon to the active pharmacy.
  // - Pharmacy mode → "<Pharmacy name> — OwlRx" + pharmacy logo as favicon
  // - SaaS / anonymous → restore the OwlRx default
  useEffect(() => {
    const defaultTitle = 'OwlRx Portal'
    const defaultIcon = '/owl.svg'

    if (mode === 'pharmacy' && activePharmacy?.name) {
      document.title = `${activePharmacy.name} — OwlRx`
    } else {
      document.title = defaultTitle
    }

    // Find or create the <link rel="icon"> tag
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    if (mode === 'pharmacy' && activePharmacy?.logo_url) {
      link.href = activePharmacy.logo_url
      // Hint the type so browsers handle SVG / data URLs correctly
      if (activePharmacy.logo_url.startsWith('data:image/svg')) {
        link.type = 'image/svg+xml'
      } else if (activePharmacy.logo_url.startsWith('data:image/png')) {
        link.type = 'image/png'
      } else if (activePharmacy.logo_url.startsWith('data:image/jpeg')) {
        link.type = 'image/jpeg'
      } else if (activePharmacy.logo_url.startsWith('data:image/webp')) {
        link.type = 'image/webp'
      } else {
        link.removeAttribute('type')
      }
    } else {
      link.href = defaultIcon
      link.type = 'image/svg+xml'
    }
  }, [mode, activePharmacy?.name, activePharmacy?.logo_url])

  const value: PharmacyState = { mode, activePharmacy, isOverride, enterPharmacy, exitPharmacy }
  return <PharmacyContext.Provider value={value}>{children}</PharmacyContext.Provider>
}

export function usePharmacy(): PharmacyState {
  const v = useContext(PharmacyContext)
  if (!v) throw new Error('usePharmacy must be used inside <PharmacyProvider>')
  return v
}
